/**
 * Reduz a logomarca do cliente para um data URL que a API aceita.
 *
 * SVG e GIF ficam de fora de propósito. O recorte cabe em 256px no maior lado
 * para o arquivo caber no limite do servidor sem um bucket de objetos.
 */

export const CLIENT_LOGO_ACCEPT = "image/png,image/jpeg,image/webp";
export const CLIENT_LOGO_MAX_FILE_BYTES = 4 * 1024 * 1024;
export const CLIENT_LOGO_MAX_EDGE = 256;
export const CLIENT_LOGO_MAX_DATA_URL_CHARS = 100_000;

export function describeClientLogoFileError(file: File): string | null {
  if (file.type === "image/svg+xml" || file.type === "image/gif") {
    return "Use um arquivo PNG, JPEG ou WebP.";
  }
  if (!file.type.startsWith("image/")) {
    return "O arquivo precisa ser uma imagem.";
  }
  if (file.size > CLIENT_LOGO_MAX_FILE_BYTES) {
    return "A logomarca pode ter no máximo 4 MB.";
  }
  return null;
}

export async function fileToClientLogoDataUrl(file: File): Promise<string> {
  const invalid = describeClientLogoFileError(file);
  if (invalid !== null) throw new Error(invalid);

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(
    CLIENT_LOGO_MAX_EDGE / bitmap.width,
    CLIENT_LOGO_MAX_EDGE / bitmap.height,
    1,
  );
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    bitmap.close();
    throw new Error("Não foi possível processar a imagem neste navegador.");
  }
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const webp = canvas.toDataURL("image/webp", 0.86);
  if (webp.startsWith("data:image/webp") && webp.length <= CLIENT_LOGO_MAX_DATA_URL_CHARS) {
    return webp;
  }
  const png = canvas.toDataURL("image/png");
  if (png.length <= CLIENT_LOGO_MAX_DATA_URL_CHARS) return png;
  const jpeg = canvas.toDataURL("image/jpeg", 0.82);
  if (jpeg.length <= CLIENT_LOGO_MAX_DATA_URL_CHARS) return jpeg;
  throw new Error("A logomarca ficou grande demais. Use um arquivo mais simples.");
}
